import { SEO } from '../components/SEO';
import { FaShieldAlt, FaUsers, FaAward, FaHeart } from 'react-icons/fa';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About SpinEmerge',
  description: 'Learn about SpinEmerge - your trusted guide to UK casino reviews and offers'
};

export const About = () => {
  return (
    <>
      <SEO
        title="About Us - SpinEmerge Casino Reviews"
        description="Learn about SpinEmerge, your trusted source for UK casino reviews, bonuses, and expert insights. We help players make informed decisions."
        canonical="/about"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-20 pb-20">
        {/* Hero Section */}
        <section className="py-12 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                About SpinEmerge
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Your trusted guide to finding the best UK casino offers and making informed gaming decisions
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="space-y-12">
              {/* Our Mission */}
              <div className="bg-dark-800 rounded-xl p-8 border border-gray-700">
                <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  At SpinEmerge, we're dedicated to helping UK players navigate the vast world of online casinos. 
                  Our mission is simple: provide honest, comprehensive reviews and comparisons that empower you 
                  to make the best choices for your gaming experience.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We understand that choosing the right casino can be overwhelming. With hundreds of options, 
                  varying bonuses, and different terms, it's easy to feel lost. That's where we come in. 
                  We do the research, test the platforms, and break down everything you need to know in clear, 
                  easy-to-understand reviews.
                </p>
              </div>

              {/* What We Do */}
              <div className="bg-dark-800 rounded-xl p-8 border border-gray-700">
                <h2 className="text-3xl font-bold text-white mb-6">What We Do</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaShieldAlt className="text-primary-400 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Expert Reviews</h3>
                      <p className="text-gray-400">
                        Our team of casino experts thoroughly tests each platform, evaluating games, 
                        bonuses, customer support, and overall user experience.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaAward className="text-primary-400 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Bonus Analysis</h3>
                      <p className="text-gray-400">
                        We break down bonus terms, wagering requirements, and restrictions so you know 
                        exactly what you're getting into before you sign up.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaUsers className="text-primary-400 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Player-Focused</h3>
                      <p className="text-gray-400">
                        Every review is written from a player's perspective, focusing on what matters 
                        most: fairness, security, game quality, and payout speed.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaHeart className="text-primary-400 text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Responsible Gaming</h3>
                      <p className="text-gray-400">
                        We promote responsible gambling and only feature casinos licensed by the UK 
                        Gambling Commission that prioritize player safety.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Our Values */}
              <div className="bg-dark-800 rounded-xl p-8 border border-gray-700">
                <h2 className="text-3xl font-bold text-white mb-6">Our Values</h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-primary-500 pl-4">
                    <h3 className="text-xl font-semibold text-white mb-2">Transparency</h3>
                    <p className="text-gray-400">
                      We're upfront about our relationships with casinos and always prioritize honest, 
                      unbiased reviews over promotional content.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent-500 pl-4">
                    <h3 className="text-xl font-semibold text-white mb-2">Accuracy</h3>
                    <p className="text-gray-400">
                      Our information is regularly updated and verified. We test platforms ourselves 
                      and cross-reference with player feedback.
                    </p>
                  </div>
                  <div className="border-l-4 border-primary-500 pl-4">
                    <h3 className="text-xl font-semibold text-white mb-2">Player First</h3>
                    <p className="text-gray-400">
                      Your interests come first. We highlight both the positives and negatives, 
                      helping you make informed decisions that suit your preferences.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-r from-primary-500/10 to-accent-500/10 rounded-xl p-8 border border-primary-500/20 text-center">
                <h2 className="text-3xl font-bold text-white mb-4">Have Questions?</h2>
                <p className="text-gray-300 mb-6">
                  We're here to help. If you have questions about our reviews or need assistance 
                  finding the right casino, don't hesitate to reach out.
                </p>
                <a
                  href="mailto:info@spinemerge.com"
                  className="inline-block bg-gradient-to-r from-primary-500 to-accent-500 text-white font-semibold py-3 px-8 rounded-lg hover:from-primary-600 hover:to-accent-600 transition-all duration-200"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

